import React, {useEffect, useState} from 'react'
import {_} from 'plugins/i18n'
import Text from 'app/Components/twComponents/Text'
import {cn} from 'app/lib'

export const NumberIcon = ({
	step,
	maxStep,
	disabled,
}: {
	step?: number
	maxStep?: number
	disabled?: boolean
}) => {
	return (
		<div
			className={`relative flex h-10 w-10 items-center justify-center rounded-full border border-solid p-1 ${
				disabled
					? 'border-silver text-capGreyFg'
					: 'border-onBackground bg-onPrimary text-onBackground'
			}`}
		>
			{maxStep ? (
				<>
					{/* Step number - top left */}
					<Text
						variant="base"
						className="absolute top-1.5 left-2 text-lg leading-none font-bold"
					>
						{step}
					</Text>
					{/* Diagonal line */}
					<div className="absolute bottom-4 left-3 h-px w-5 -rotate-60 transform bg-current" />
					{/* Max step number - bottom right */}
					<Text
						variant="base"
						className="absolute right-2 bottom-1.5 text-xs leading-none font-bold"
					>
						{maxStep}
					</Text>
				</>
			) : (
				<Text variant="headingXL">{step}</Text>
			)}
		</div>
	)
}

const DefaultTitle = ({title}: {title: string}): JSX.Element => {
	return (
		<Text HtmlTag="h2" variant="heading2XL">
			{_(title)}
		</Text>
	)
}

export const AccordionChevronIcon = ({isOpen}: {isOpen: boolean}) => {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			className={`h-8 w-8 transform transition-transform duration-300 ${
				isOpen ? 'rotate-180' : ''
			}`}
			fill="none"
			viewBox="0 0 24 24"
			stroke="currentColor"
		>
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				strokeWidth={2}
				d="M5 15l7-7 7 7"
			/>
		</svg>
	)
}

export const SectionTitle = ({title}): JSX.Element => {
	return (
		<Text HtmlTag="h2" variant="headingXL">
			{_(title)}
		</Text>
	)
}

export const Accordion = ({
	children,
	classNameWrap = '',
	classNameInner = '',
	disabled,
	fullBorder = true,
	isOpenInit = false,
	showIcon = true,
	step,
	maxStep,
	title = '',
	Title = DefaultTitle,
}: {
	children: React.ReactNode
	classNameWrap?: string
	classNameInner?: string
	disabled?: boolean
	fullBorder?: boolean
	isOpenInit?: boolean
	showIcon?: boolean
	step?: number
	maxStep?: number
	title?: string
	Title?: (props: {readonly title: string}) => JSX.Element
} & React.HTMLProps<HTMLDivElement>) => {
	const [isOpen, setIsOpen] = useState(isOpenInit)

	useEffect(() => {
		if (isOpen !== isOpenInit) setIsOpen(isOpenInit)
	}, [isOpenInit])

	const toggleAccordion = () => {
		setIsOpen(!isOpen)
	}
	return (
		<div
			className={cn(
				`border-silver flex w-full border-solid px-8 py-8`,
				disabled ? 'text-tertiary' : 'text-onBackground',
				fullBorder ? `rounded-lg border` : `border-b`,
				classNameWrap
			)}
		>
			{showIcon && (
				<Text className="hidden pt-1 pr-8 lg:block" variant="headingXL">
					<NumberIcon step={step} maxStep={maxStep} disabled={disabled} />
				</Text>
			)}
			<div className="w-full">
				<button
					onClick={toggleAccordion}
					type="button"
					disabled={disabled}
					className={`justify-left flex w-full flex-col ${
						disabled ? 'cursor-not-allowed' : 'cursor-pointer'
					}`}
				>
					{step && (
						<Text variant="base" className="text-left lg:hidden">
							<span className="font-bold">
								{_`Step`} {step}
							</span>{' '}
							{_`of`} 4
						</Text>
					)}
					<div className="flex w-full items-center justify-between">
						<Title title={title} />
						<AccordionChevronIcon isOpen={isOpen} />
					</div>
				</button>
				{isOpen && <div className={cn(classNameInner)}>{children}</div>}
			</div>
		</div>
	)
}

export const SummaryCard = ({
	children,
	classNameWrap = '',
	classNameInner = '',
	fullBorder = true,
	title = '',
	Title = SectionTitle,
}: {
	children: React.ReactNode
	classNameWrap?: string
	classNameInner?: string
	fullBorder?: boolean
	title: string
	Title?: (props: {readonly title: string}) => JSX.Element
}) => {
	return (
		<div
			className={cn(
				'text-onBackground p-4',
				fullBorder && `border-silver rounded-lg border`,
				classNameWrap
			)}
		>
			<div className={cn('flex w-full items-center justify-between')}>
				<Title title={title} />
			</div>
			<div className={cn(classNameInner)}>{children}</div>
		</div>
	)
}

export const CardOrAccordion = ({
	children,
	classNameWrap = '',
	classNameInner = 'flex flex-col gap-2 pt-3',
	isOpenInit = false,
	title = '',
	Title = SectionTitle,
}: {
	children: React.ReactNode
	classNameWrap?: string
	classNameInner?: string
	isOpenInit?: boolean
	title: string
	Title?: (props: {readonly title: string}) => JSX.Element
}) => {
	return (
		<div className={cn(classNameWrap)}>
			<SummaryCard
				classNameWrap="hidden lg:block "
				classNameInner={classNameInner}
				Title={Title}
				title={title}
			>
				{children}
			</SummaryCard>
			<Accordion
				classNameWrap="py-4 lg:hidden"
				classNameInner={classNameInner}
				fullBorder={false}
				isOpenInit={isOpenInit}
				Title={Title}
				title={title}
			>
				{children}
			</Accordion>
		</div>
	)
}
